import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import MultiSelect from '@kenshooui/react-multi-select'
import { isEmpty } from 'lodash'

import useStyles from './styles'
import * as service from 'service'

const RecipientsDialog = ({
  open,
  onClose,
  onConfirm,
  departments,
  isDepartmentsLoading,
  initialRecipientGroups,
}) => {
  const classes = useStyles()

  const [selectedDepartments, setSelectedDepartments] = useState([])
  const [userItems, setUserItems] = useState([])
  const [isUsersLoading, setIsUsersLoading] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState([])
  const [pendingUserIds, setPendingUserIds] = useState(new Set())

  useEffect(() => {
    if (open) {
      const groups = initialRecipientGroups || []
      setSelectedDepartments(
        groups.map((group) => ({ id: group.id, name: group.name })),
      )
      setPendingUserIds(
        new Set(groups.flatMap((group) => group.users.map((user) => user.id))),
      )
    } else {
      setSelectedDepartments([])
      setSelectedUsers([])
      setUserItems([])
      setPendingUserIds(new Set())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (pendingUserIds.size > 0 && userItems.length > 0) {
      setSelectedUsers(
        userItems.filter((item) => pendingUserIds.has(item.userId)),
      )
      setPendingUserIds(new Set())
    }
  }, [userItems, pendingUserIds])

  useEffect(() => {
    if (!selectedDepartments.length) {
      setUserItems([])
      return
    }

    const fetchUsers = async () => {
      setIsUsersLoading(true)
      try {
        const responses = await Promise.all(
          selectedDepartments.map((department) =>
            service.dponet.users.get({
              departmentsIdIn: [department.id],
              minimal: true,
              active: true,
              perPage: 10000,
            }),
          ),
        )

        const items = []
        selectedDepartments.forEach((department, index) => {
          const users = responses[index]?.data?.users || []
          users.forEach((user) => {
            items.push({
              id: `${department.id}-${user.id}`,
              label: user.name,
              email: user.email,
              group: department.name,
              userId: user.id,
              departmentId: department.id,
            })
          })
        })

        setUserItems(items)
      } catch {
        setUserItems([])
      } finally {
        setIsUsersLoading(false)
      }
    }

    fetchUsers()
  }, [selectedDepartments])

  const handleDepartmentsChange = (_event, value) => {
    setSelectedDepartments(value)
  }

  const allDepartmentsSelected =
    !isEmpty(departments) && selectedDepartments.length === departments.length

  const handleToggleAllDepartments = () => {
    if (allDepartmentsSelected) {
      setSelectedDepartments([])
      return
    }
    setSelectedDepartments(
      departments.map((department) => ({
        id: department.id,
        name: department.name,
      })),
    )
  }

  const handleConfirm = () => {
    const recipientGroups = selectedDepartments
      .map((department) => ({
        id: department.id,
        name: department.name,
        users: selectedUsers
          .filter((user) => user.departmentId === department.id)
          .map((user) => ({
            id: user.userId,
            label: user.label,
            email: user.email,
          })),
      }))
      .filter((group) => group.users.length > 0)

    const uniqueRecipients = [
      ...new Map(
        selectedUsers.map((user) => [
          user.userId,
          { id: user.userId, label: user.label, email: user.email },
        ]),
      ).values(),
    ]

    onConfirm(recipientGroups, uniqueRecipients)
    onClose()
  }

  const multiSelectMessages = {
    searchPlaceholder: 'Buscar usuário',
    noItemsMessage: isUsersLoading
      ? 'Carregando...'
      : 'Nenhum usuário encontrado',
    noneSelectedMessage: isUsersLoading
      ? 'Carregando...'
      : 'Nenhum usuário selecionado',
    selectedMessage: 'selecionado(s)',
    selectAllMessage: 'Selecionar Todos',
    clearAllMessage: 'Limpar Todos',
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle disableTypography>
        <div className={classes.dialogTitleContainer}>
          <Typography variant="h3">Adicionar participantes</Typography>
        </div>
      </DialogTitle>

      <DialogContent>
        <Box mb={2}>
          <Box
            mb={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h4">Selecione um setor</Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleToggleAllDepartments}
              disabled={isDepartmentsLoading || isEmpty(departments)}
            >
              {allDepartmentsSelected
                ? 'Limpar departamentos'
                : 'Selecionar todos os departamentos'}
            </Button>
          </Box>
          <Autocomplete
            multiple
            options={departments}
            getOptionLabel={(option) => option.name}
            getOptionSelected={(option, value) => option.id === value.id}
            value={selectedDepartments}
            onChange={handleDepartmentsChange}
            loading={isDepartmentsLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filtrar por departamento"
                variant="outlined"
                fullWidth
              />
            )}
          />
        </Box>

        <MultiSelect
          items={userItems}
          selectedItems={selectedUsers}
          onChange={setSelectedUsers}
          messages={multiSelectMessages}
          wrapperClassName={classes.multiSelect}
          withGrouping
        />
      </DialogContent>

      <DialogActions>
        <div className={classes.dialogActions}>
          <Button onClick={onClose} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={handleConfirm} color="primary" variant="contained">
            Salvar
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  )
}

export default RecipientsDialog
